import { useStore } from 'vuex'
import { useInboxFiltersStore } from '@app/application/communication/screens/inbox/store/inbox-filters'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { keyBy, uniqBy } from 'lodash'
import {
  CLIENT_MEETING_CATEGORY,
  combineNotesAndTodos,
  COMPANY_CATEGORY,
  filterRemarks,
  groupCommunicationsByContext,
} from '@app/application/communication/screens/inbox/store/utils'
import * as mutationTypes from '@app/application/communication/screens/inbox/store/mutation-types'
import { SfLog } from '@app/adapters/logger'
import { NoteTypeOption } from '@app/application/communication/screens/inbox/store/constants'
import { RemarkContextTypes } from '@app/application/communication/types'

export default function useGroupedFilteredCommunications() {
  const store = useStore()
  const inboxFiltersStore = useInboxFiltersStore()

  const { noteType, tags, authors, searchTerm } = storeToRefs(inboxFiltersStore)

  const anyUnfilteredCommunications = computed(() => {
    return (
      Object.values(store.getters.notes).length > 0 ||
      Object.values(store.getters.todos).length > 0
    )
  })

  const filteredNotes = computed(() => {
    if (
      !(
        noteType.value === null ||
        noteType.value === NoteTypeOption.NOTE ||
        noteType.value === NoteTypeOption.MENTIONS
      )
    ) {
      return {}
    }
    const notes = Object.values(store.getters.notes)
    return keyBy(
      filterRemarks(
        notes,
        searchTerm.value.toLowerCase(),
        noteType.value,
        tags.value,
        authors.value,
        store.getters.commentById,
        store.getters.userById
      ),
      'id'
    )
  })

  const filteredTodos = computed(() => {
    if (
      !(
        noteType.value === null ||
        noteType.value === NoteTypeOption.TODO ||
        noteType.value === NoteTypeOption.OPEN_TODO ||
        noteType.value === NoteTypeOption.MENTIONS
      )
    ) {
      return {}
    }
    return keyBy(
      filterRemarks(
        Object.values(store.getters.todos)
          .concat(Object.values(store.getters.sfAssistantCheckTodos))
          .filter(
            (todo) => noteType.value !== NoteTypeOption.OPEN_TODO || !todo.done
          ),
        searchTerm.value.toLowerCase(),
        noteType.value,
        tags.value,
        authors.value,
        store.getters.commentById,
        store.getters.userById
      ),
      'id'
    )
  })

  const filterByContext = computed(
    () =>
      store.state.contextType === 'company_workflow' && !!store.state.contextId
  )

  function groupedItems(items) {
    return groupCommunicationsByContext(
      items,
      store.state.ledgerId,
      store.getters.contextByLedgerTypeAndId,
      store.state.sections.workflows,
      // On the company inbox (no single workflow scope) collapse a remark
      // shared by multiple workflows to one canonical workflow so it is not
      // rendered once per workflow group.
      !filterByContext.value
    )
  }

  const groupedNotes = computed(() =>
    groupedItems(Object.values(filteredNotes.value))
  )
  const groupedReadNotes = computed(() =>
    groupedItems(
      Object.values(filteredNotes.value).filter((n) => {
        const notification = store.getters.notificationsByRemarkId[n.id]
        return (
          !notification ||
          !store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )
  const groupedUnreadNotes = computed(() =>
    groupedItems(
      Object.values(filteredNotes.value).filter((n) => {
        const notification = store.getters.notificationsByRemarkId[n.id]
        return (
          !!notification &&
          !!store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )
  const groupedTodos = computed(() =>
    groupedItems(Object.values(filteredTodos.value))
  )
  const groupedReadTodos = computed(() =>
    groupedItems(
      Object.values(filteredTodos.value).filter((t) => {
        const notification = store.getters.notificationsByRemarkId[t.id]
        return (
          !notification ||
          !store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )
  const groupedUnreadTodos = computed(() =>
    groupedItems(
      Object.values(filteredTodos.value).filter((t) => {
        const notification = store.getters.notificationsByRemarkId[t.id]
        return (
          !!notification &&
          !!store.state.notifications.initialUnreadStateById[notification.id]
        )
      })
    )
  )

  const groupedCommunication = computed(() =>
    combineNotesAndTodos(groupedNotes.value, groupedTodos.value)
  )
  const groupedRead = computed(() =>
    combineNotesAndTodos(groupedReadNotes.value, groupedReadTodos.value)
  )
  const groupedUnread = computed(() =>
    combineNotesAndTodos(groupedUnreadNotes.value, groupedUnreadTodos.value)
  )

  const filteredUnreadNotificationCount = computed(
    () =>
      uniqBy(
        Object.values(groupedUnread.value).flatMap((category) =>
          Object.values(category).flatMap((item) =>
            item.map((comm) => store.getters.notificationsByRemarkId[comm.id])
          )
        )
        // We need this extra filter, because we sometimes want to keep read items around in the
        // unread section. The counter should still reflect the actual number of unread items though.
      ).filter((notification) => !notification.read).length
  )

  const workflowToShow = computed(() => {
    if (filterByContext.value) {
      return store.getters.workflowsById[store.state.contextId]?.name
    } else {
      return null
    }
  })

  const sections = computed(() => store.getters.sections)

  function getNotificationForCommsItem(comm) {
    return store.getters.notificationsByRemarkId[comm.id]
  }

  function sectionHasActualUnread(sectionId) {
    return Object.values(groupedUnread.value[sectionId] || {}).reduce(
      (prev, items) =>
        prev ||
        items.reduce(
          (prev, comm) => prev || !getNotificationForCommsItem(comm).read,
          false
        ),
      false
    )
  }

  const menuSections = computed(() => {
    return sections.value
      .filter((s) => !!groupedCommunication.value[s.categoryId])
      .filter(
        (s) =>
          !filterByContext.value ||
          s.categoryId === workflowToShow.value ||
          s.categoryId === RemarkContextTypes.DOCUMENT
      )
      .map((s) => ({
        ...s,
        hasUnread: sectionHasActualUnread(s.categoryId),
      }))
  })

  const clientOverviewHasUnread = computed(() => {
    return sectionHasActualUnread(COMPANY_CATEGORY)
  })

  const clientMeetingHasUnread = computed(() => {
    return sectionHasActualUnread(CLIENT_MEETING_CATEGORY)
  })

  const sectionItems = computed(() => {
    const result = {}
    for (const section of menuSections.value) {
      result[section.categoryId] = Object.keys(
        groupedCommunication.value[section.categoryId]
      )
        .sort()
        .map((name) => ({
          name,
          hasUnread: (
            groupedUnread.value[section.categoryId]?.[name] || []
          ).reduce(
            (prev, comm) => prev || !getNotificationForCommsItem(comm).read,
            false
          ),
        }))
    }
    return result
  })

  const talkingPointBucketUnread = computed(() => {
    const result = {}
    const buckets = groupedUnread.value[CLIENT_MEETING_CATEGORY] || {}
    for (const bucket of Object.keys(buckets)) {
      result[bucket] = (buckets[bucket] || []).some(
        (comm) => !getNotificationForCommsItem(comm).read
      )
    }
    return result
  })

  async function markCategoryAsRead(categoryId) {
    store.commit(mutationTypes.SET_MARKING_AS_READ, true)
    const notifications = Object.values(
      groupedUnread.value[categoryId]
    ).flatMap((item) =>
      item.map((comm) => store.getters.notificationsByRemarkId[comm.id])
    )
    try {
      const promises = []
      for (const notification of notifications) {
        if (!notification.read) {
          promises.push(
            store.dispatch('toggleReadNotification', notification.id)
          )
        } else {
          store.commit(
            mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID,
            notification.id
          )
        }
      }
      await Promise.all(promises)
      window.eventHub.$emit('resetNotificationCount')
    } catch (error) {
      SfLog.info('Failed to toggle notification as read', {
        extra: {
          ...window.Silverfin.jsonParams,
          error,
        },
      })
    } finally {
      store.commit(mutationTypes.SET_MARKING_AS_READ, false)
    }
  }

  async function markAllAsRead() {
    store.commit(mutationTypes.SET_MARKING_AS_READ, true)
    const notifications = Object.values(groupedUnread.value).flatMap(
      (category) =>
        Object.values(category).flatMap((item) =>
          item.map((comm) => store.getters.notificationsByRemarkId[comm.id])
        )
    )
    try {
      const promises = []
      for (const notification of uniqBy(notifications, 'id')) {
        if (!notification.read) {
          promises.push(
            store.dispatch('toggleReadNotification', notification.id)
          )
        } else {
          store.commit(
            mutationTypes.RESET_NOTIFICATION_READ_CACHE_FOR_ID,
            notification.id
          )
        }
      }
      await Promise.all(promises)
      window.eventHub.$emit('resetNotificationCount')
    } catch (error) {
      SfLog.info('Failed to toggle notification as read', {
        extra: {
          ...window.Silverfin.jsonParams,
          error,
        },
      })
    } finally {
      store.commit(mutationTypes.SET_MARKING_AS_READ, false)
    }
  }

  return {
    anyUnfilteredCommunications,
    filteredUnreadNotificationCount,
    groupedCommunication,
    groupedRead,
    groupedUnread,
    filterByContext,
    menuSections,
    sectionItems,
    clientOverviewHasUnread,
    clientMeetingHasUnread,
    talkingPointBucketUnread,
    markCategoryAsRead,
    markAllAsRead,
  }
}
